import type {
	MappedZone,
	SetZoneParams,
	ZoneApiResponse
} from '@/components/zones/types'
import { useAuditLogs } from '@/composables/useAuditLogs'
import { useAuth } from '@/composables/useAuth'
import { useErrorReporter } from '@/composables/useErrorReporter'
import { useToast } from '@/composables/useToast'
import type { BillablePackage } from '@/constants/accounts/billables'
import {
	API_BASE,
	API_ENDPOINT_PORTAL_ZONE,
	API_ENDPOINT_PORTAL_ZONES, API_ENDPOINT_PORTAL_ZONES_BILLABLES
} from '@/constants/index.js'
import { ZONE_COLORS } from '@/constants/zones'
import { mapZone } from '@/utils/mappers/ZoneMap'
import { computed, ref } from 'vue'

export const ZONE_STATUSES = [
	{
		name: 'In Service',
		id: 'in_service'
	},
	{
		name: 'Extended',
		id: 'extended'
	},
	{
		name: 'Non-Public',
		id: 'nonpublic'
	},
	{
		name: 'Reference',
		id: 'reference'
	},
	{
		name: 'Remote',
		id: 'remote'
	}
]

const zones = ref<MappedZone[]>([])
const zoneBillables = ref<BillablePackage[]>([])
const loading = ref(true)
const zone = ref<MappedZone | null>()
const parentZoneOptions = computed(() => {
	const parentZones = zones.value?.map(zone => {
		return {
			name: zone.name,
			id: zone.id
		}
	}) || []
	return [ {
		name: 'None',
		id: '0'
	}, ...parentZones ]
})

export const useZones = () => {
	const { bugsnagReport } = useErrorReporter()
	const { userData } = useAuth()
	const { addToast } = useToast()
	const error = useError()

	const getZones = async (refetch?: boolean) => {
		if (!userData.value) {
			return
		} // Don't fetch if user data is not available
		if (zones.value?.length && !refetch) {
			return
		} // Don't fetch if zones are already loaded
		loading.value = true

		try {
			await $fetch(`${API_BASE}${API_ENDPOINT_PORTAL_ZONES}`, {
				method: 'GET',
				credentials: 'include',
				onResponse({ response }) {
					if (response.ok && response.status === 200) {
						const responseData: ZoneApiResponse[] = response._data
						zones.value = responseData?.map(zone => mapZone(zone)) as unknown as MappedZone[]
					} else {
						const responseError = response._data?.errors?.[0]
						throw new Error(`getZones: No data or failed with status ${response.status}${responseError ? ` - ${responseError}` : ''}`)
					}
				}
			})
		} catch (e) {
			bugsnagReport({
				error: e instanceof Error ? e : new Error('getZones - catch: ' + (e as Error).toString()),
				showToast: true
			})
		} finally {
			loading.value = false
		}
	}

	const getZoneBillables = async (zoneId: string) => {
		loading.value = true
		if (!zoneId) {
			loading.value = false
			return
		}
		try {
			await $fetch(`${API_BASE}${API_ENDPOINT_PORTAL_ZONES_BILLABLES.replace('{zone-id}', zoneId)}`, {
				method: 'GET',
				credentials: 'include',
				onResponse({ response }) {
					if (response.ok && response.status === 200) {
						const responseData: BillablePackage[] = response._data
						loading.value = false
						zoneBillables.value = responseData
					} else {
						const responseError = response._data?.errors?.[0]
						throw new Error(`getZoneBillables: No data or failed with status ${response.status}${responseError ? ` - ${responseError}` : ''}`)
					}
				}
			})
		} catch (e) {
			bugsnagReport({
				error: e instanceof Error ? e : new Error('getZoneBillables - catch: ' + (e as Error).toString()),
				showToast: true
			})
		} finally {
			loading.value = false
		}
	}

	const getZone = async (zoneId: string, resetIfNoId?: boolean) => {
		loading.value = true
		if (!zoneId) {
			if (resetIfNoId) {
				zone.value = null
			}
			loading.value = false
			return
		}
		try {
			await $fetch(`${API_BASE}${API_ENDPOINT_PORTAL_ZONE.replace('{zone-id}', zoneId)}`, {
				method: 'GET',
				credentials: 'include',
				onResponse({ response }) {
					if (!response.ok) {
						const responseError = response._data?.errors?.[0]
						throw new Error(`getZone: No data or failed with status ${response.status}${responseError ? ` - ${responseError}` : ''}`)
					}
					if (response.status === 204) {
						error.value = createError({
							statusCode: 204,
							statusMessage: 'Zone not found.'
						})
						return
					}
					zone.value = mapZone(response._data)
				}
			})
		} catch (e) {
			bugsnagReport({
				error: e instanceof Error ? e : new Error('getZone - catch: ' + (e as Error).toString()),
				showToast: true
			})
		} finally {
			loading.value = false
		}
	}

	const setZone = async (params: SetZoneParams, redirect?: boolean) => {
		loading.value = true
		const { zoneId, body } = params
		const isNewZone = !zoneId
		try {
			if (body.points && JSON.parse(body.points)) {
				body.points = JSON.parse(body.points)
			}
		} catch (e) {
			bugsnagReport({
				error: e instanceof Error ? e : new Error('setZone - catch: ' + (e as Error).toString()),
				showToast: true,
				toast: {
					title: `Error ${isNewZone ? 'creating' : 'updating'} zone`,
					message: e instanceof Error ? e.message : `An error occurred while ${isNewZone ? 'creating' : 'updating'} the zone`
				}
			})
			return
		}
		try {
			// TODO if body.points can't resolve to an object then it wont work (we need more checks around validating actual geoJson and messaging to the user)

			if (body.points && typeof body.points !== 'object') {
				delete body.points
			}
			await $fetch(isNewZone ? `${API_BASE}${API_ENDPOINT_PORTAL_ZONES}` : `${API_BASE}${API_ENDPOINT_PORTAL_ZONE.replace('{zone-id}', zoneId)}`, {
				method: isNewZone ? 'POST' : 'PUT',
				credentials: 'include',
				body,
				async onResponse({ response }) {
					if (response.ok && response.status === 200) {
						addToast({
							title: `Zone ${isNewZone ? 'created' : 'updated'} successfully`,
							notificationType: 'success'
						})
						if (isNewZone) {
							getZones(true)
							await navigateTo('/zones')
						} else {
							const { getAuditLogs, allAuditLogs } = useAuditLogs()
							if (redirect) {
								await getAuditLogs()
								if (allAuditLogs.value?.[0]?.id) {
									await navigateTo({
										name: 'internal-zones-audit-logs-auditid',
										params: { auditid: allAuditLogs.value[0].id }
									})
								}
							}
						}
					} else {
						const responseError = response._data?.error || response._data?.errors?.[0]
						throw new Error(responseError || `setZone: Failed with status ${response.status}${responseError ? ` - ${responseError}` : ''}`)
					}
				}
			})
		} catch (e) {
			bugsnagReport({
				error: e instanceof Error ? e : new Error('setZone - catch: ' + (e as Error).toString()),
				showToast: true,
				toast: {
					title: `Error ${isNewZone ? 'creating' : 'updating'} zone`,
					message: e instanceof Error ? e.message : `An error occurred while ${isNewZone ? 'creating' : 'updating'} the zone`
				}
			})
		} finally {
			loading.value = false
		}
	}

	return {
		ZONE_COLORS,
		ZONE_STATUSES,
		zones,
		zoneBillables,
		getZones,
		getZoneBillables,
		loading,
		getZone,
		zone,
		setZone,
		parentZoneOptions
	}
}
